import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import type { Listing } from "@/types";

export function ListingForm({ listing, badges, onSaved, disabled }: { listing: Listing; badges: string[]; onSaved: (l: Listing) => void; disabled?: boolean }) {
  const [f, setF] = useState({ name: "", thumb_title: "", price: "", title: "", description: "", tags: "", badges: [] as string[] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setF({
      name: listing.name, thumb_title: listing.thumb_title, price: String(listing.price), title: listing.title,
      description: listing.description, tags: listing.tags.join(", "), badges: listing.badges,
    });
  }, [listing]);

  const set = (k: string, v: any) => setF((p) => ({ ...p, [k]: v }));
  const tagList = f.tags.split(",").map((t) => t.trim()).filter(Boolean);
  const toggleBadge = (b: string) => set("badges", f.badges.includes(b) ? f.badges.filter((x) => x !== b) : [...f.badges, b].slice(-2));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/listings/${listing.id}`, { ...f, price: Number(f.price), tags: tagList });
      onSaved(data);
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="paper-card space-y-5 p-6" data-testid="listing-form">
      <div className="grid gap-4 sm:grid-cols-[1fr_120px]">
        <label className="block"><span className="label">Internal name</span>
          <input className="field" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="field-name" /></label>
        <label className="block"><span className="label">Price ($)</span>
          <input className="field" type="number" step="0.5" min="1" value={f.price} onChange={(e) => set("price", e.target.value)} data-testid="field-price" /></label>
      </div>
      <label className="block"><span className="label">Thumbnail banner ({f.thumb_title.length}/60)</span>
        <input className="field" maxLength={60} value={f.thumb_title} onChange={(e) => set("thumb_title", e.target.value)} data-testid="field-thumb-title" /></label>
      <div>
        <span className="label">Thumbnail badges (max 2)</span>
        <div className="flex flex-wrap gap-2">
          {badges.map((b) => (
            <button key={b} type="button" onClick={() => toggleBadge(b)} className={`chip ${f.badges.includes(b) ? "chip-active" : ""}`} data-testid={`badge-toggle-${b.replace(/\W+/g, "-").toLowerCase()}`}>{b}</button>
          ))}
        </div>
      </div>
      <label className="block"><span className="label">Etsy title <span className={f.title.length > 140 ? "text-[#C2412D]" : ""}>({f.title.length}/140)</span></span>
        <textarea className="field min-h-[72px]" maxLength={140} value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="field-title" /></label>
      <label className="block"><span className="label">Description</span>
        <textarea className="field min-h-[220px] text-sm" value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="field-description" /></label>
      <div className="rounded-2xl border-2 border-dashed border-[#9CC9E8] bg-[#9CC9E8]/15 p-4" data-testid="ai-disclosure">
        <p className="mb-1 text-xs font-bold uppercase tracking-[0.16em] text-[#23324A]/70">AI disclosure (added to the end of the description)</p>
        <p className="text-sm text-[#23324A]">{listing.ai_disclosure}</p>
      </div>
      <label className="block"><span className="label">Tags, comma separated <span className={tagList.length !== 13 ? "text-[#C2412D]" : ""}>({tagList.length}/13)</span></span>
        <textarea className="field min-h-[72px] text-sm" value={f.tags} onChange={(e) => set("tags", e.target.value)} data-testid="field-tags" /></label>
      <button onClick={save} disabled={saving || disabled} className="btn-coral" data-testid="save-listing-btn"><Save className="h-4 w-4" strokeWidth={2.5} />{saving ? "Saving…" : "Save changes"}</button>
    </div>
  );
}
